'use no memo';
import React from 'react';
import { css } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { useAgentSessionsContext } from '@confluence/agent-mention/entry-points/AgentSessionsContext.ts';
import {
	type CommentData,
	type ReplyData,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import type { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import type { PageInfoNode } from '@confluence/page-info/entry-points/PageInfoQuery.ts';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { useCommentsPanelState } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import type { ViewReplyOptions } from '@confluence/comments-common/entry-points/repliesHelpers.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { formatTimestampForAriaLabel } from '@confluence/comments-util/entry-points/dateUtils.ts';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';

import { ObjectComment } from './ObjectComment.tsx';
import { ObjectCommentReplyEditor } from './ObjectCommentReplyEditor.tsx';

const replyBoxStyles = css({
	listStyle: 'none',
	position: 'relative',
	paddingLeft: token('space.300'),
});

const replyBoxStylesV2 = css({
	paddingLeft: token('space.200'),
});

const objectCommentWrapperStyles = css({
	paddingBottom: token('space.100'),
});

const nestedReplyThreadBoxStyles = css({
	display: 'flex',
	flexDirection: 'column',
	marginLeft: token('space.200'), // margin vs padding to get border to align correctly when present
});

const nestedReplyThreadBoxStylesV2 = css({
	paddingLeft: token('space.250'),
});

const topLevelReplyThreadBoxStyles = css({
	display: 'flex',
	flexDirection: 'column',
});

const withLeftBorderStyle = css({
	borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const withLeftBorderStyleUpdated = css({
	borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const withLeftBoxShadow = css({
	boxShadow: `inset 0.08rem 0 0 0 ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const footerReplyContainerStyle = css({
	position: 'relative',
	'&::before': {
		content: "''",
		position: 'absolute',
		left: '0px',
		top: '0px',
		bottom: '0px',
		borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	},
});

const footerReplyContainerStyleUpdated = css({
	'&::before': {
		borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	},
});

// Curved branch styles for connecting replies
const replyBranchStyles = css({
	position: 'relative',
	'&::before': {
		content: "''",
		position: 'absolute',
		left: '0px', // Align with the border position
		top: '0px',
		width: '12.3px',
		height: '24px',
		borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		borderBottom: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		borderBottomLeftRadius: token('radius.large'),
		boxSizing: 'border-box',
	},
});

const replyBranchStylesUpdated = css({
	'&::before': {
		borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		borderBottom: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	},
});

const i18n = defineMessages({
	replyAriaLabel: {
		id: 'object-comments.reply.aria-label',
		defaultMessage: 'Reply by {authorName}, {timestamp}',
		description: 'Aria label for reply container announcing the author',
	},
});

export const ObjectCommentNestableReplies = ({
	parentReplyId,
	parentToRepliesMap,
	canAddComments,
	pageInfo,
	pageMode,
	threadKey,
	supportedTopLevelActions,
	isTopLevelReply,
	parentComment,
	parentReplyButtonAllowed,
	setReplyView,
	isOffline,
	flags,
	isEditorExpanded,
}: {
	parentReplyId: string;
	parentToRepliesMap: { [key: string]: ReplyData[] };
	canAddComments: boolean;
	pageInfo: PageInfoNode | null;
	pageMode: PageMode;
	threadKey: string;
	supportedTopLevelActions: CommentAction[];
	isTopLevelReply: boolean;
	parentComment: CommentData;
	parentReplyButtonAllowed: boolean;
	setReplyView: (view: ViewReplyOptions) => void;
	isOffline?: boolean;
	flags?: FlagsStateContainer;
	isEditorExpanded: boolean;
}): React.JSX.Element => {
	const { replyToCommentId, location, footerEditCommentIds } = useCommentsPanelState();
	const { hasNestedComments } = useProcessedCommentsDataState();

	const intl = useIntl();
	const { formatMessage } = intl;

	// A reply renders its agent-session status (AvatarWithActionButton) below itself. Read the
	// page's agent-session map so we can keep the thread's connector line visible for such a
	// reply (mirrors ObjectCommentThread's parentHasAgentSession for the top-level comment) —
	// otherwise the status floats with no line when the reply is the last in the thread.
	const agentSessions = useAgentSessionsContext();
	const { unreadCommentIDSet } = useUnreadCommentsState();

	const isParentCommentOpen = parentComment.isOpen;

	const repliesToRender = parentToRepliesMap[parentReplyId];

	return (
		<div
			data-testId="footer-reply-container"
			role="list"
			css={[
				isTopLevelReply ? topLevelReplyThreadBoxStyles : nestedReplyThreadBoxStyles,
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				isTopLevelReply &&
					!expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
					footerReplyContainerStyle,
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				isTopLevelReply &&
					!expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
					fg('confluence_comment_border_migration') &&
					footerReplyContainerStyleUpdated,
			]}
		>
			{repliesToRender.map((reply, idx) => {
				// These all control the visibility of the reply editor and left border
				// Generally speaking, we want to show the left border at the thread level rather than from comment body (showLeftBorder)
				// However, if one of the comment's will not have a left border (for example, if it's the last reply), we need to show at the comment level (!hideBranchingStyle)
				const replyHasChildren = parentToRepliesMap[reply.id]?.length > 0;

				const isLastReply = idx === repliesToRender.length - 1;

				const isFooterReplyMatchingReplyToCommentParameter =
					reply.id === replyToCommentId && location === LocationValue.FOOTER;

				const showReplyEditor = hasNestedComments && isFooterReplyMatchingReplyToCommentParameter;

				const parentReplyButtonVisible = parentReplyButtonAllowed && isTopLevelReply;

				// This reply renders an agent-session status row below it; used to keep the connector
				// line visible. Gate inlined, ordered last per feature-gating rules.
				const replyHasAgentSession =
					Boolean(agentSessions.get(reply.id)) &&
					expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true);

				const replyHasNestedObjects = replyHasChildren || showReplyEditor;

				// The agent status row adds height below the reply that the thread's connector line
				// must span. Treat it like a nested object for the BORDER only (not the body margin
				// `replyHasNestedObjects`, which would shift layout) — otherwise the line breaks
				// between this reply's status row and the next reply.
				const replyHasNestedObjectsOrAgent = replyHasNestedObjects || replyHasAgentSession;

				const replyIsBeingEdited = footerEditCommentIds?.has(reply.id);

				const showLeftBorder =
					parentReplyButtonVisible ||
					(replyHasNestedObjectsOrAgent && !isLastReply) ||
					(replyIsBeingEdited && !isLastReply);

				// Keep the connector line visible when the reply has an agent session, even when it is
				// the last reply (where branching is normally hidden), so the status row stays attached.
				const hideBranchingStyle =
					!replyHasAgentSession &&
					(expValEquals('confluence_comments_tuneup', 'isEnabled', true)
						? isLastReply && (!isTopLevelReply || !isEditorExpanded)
						: parentReplyButtonVisible ||
							replyHasNestedObjects ||
							(!parentReplyButtonVisible && isLastReply));

				// This moves the branching style 1px to the left to account for the 1px left border of the thread
				const adjustForFooterNestedReplies =
					hideBranchingStyle && (!isLastReply || parentReplyButtonVisible);

				// This accounts for the 1px missing border offset of the last reply in a deeply nested thread
				const isLastNestedFooterReply = isLastReply && !isTopLevelReply;

				return (
					<div
						css={[
							replyBoxStyles,
							expValEquals('confluence_comments_tuneup', 'isEnabled', true) && replyBoxStylesV2,
							// Apply curved branch styles for non-last replies
							replyBranchStyles,
							// eslint-disable-next-line confluence-feature-gating/no-preconditioning
							fg('confluence_comment_border_migration') && replyBranchStylesUpdated,
							showLeftBorder &&
								replyHasNestedObjectsOrAgent &&
								(!isLastReply ||
									(isLastReply && isEditorExpanded) ||
									!expValEquals('confluence_comments_tuneup', 'isEnabled', true)) &&
								withLeftBoxShadow,
							replyIsBeingEdited &&
								!isLastReply &&
								expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
								withLeftBoxShadow,
						]}
						// Note: Cannot set `key` to `reply.id`, which, when optimistic save (COMMENTS-6792)
						// is enabled, is the fake optimistic id first and then gets updated to the actual
						// new comment id after mutation succeeds.
						// When mutation succeeds, DOM tree is updated. If `key` stays the same, the update
						// is recognized as multiple individual "mutation:attribute" for known attributes
						// excluded from VC calculation (e.g. data-testid); if key changes, the update is
						// recognized as a replacement of the entire element tree and counted in VC.
						// Unlike `reply.id`, `idx` is stable as long as the reply's position in the list
						// does not change.
						key={`${LocationValue.FOOTER}-comment-thread-${parentComment.id}-replies-${idx}`}
						id={`${LocationValue.FOOTER}-comment-thread-${reply.id}-replies`}
						data-testid={`comment-thread-${parentReplyId}-replies-${reply.id}`}
						role="listitem"
						aria-label={formatMessage(i18n.replyAriaLabel, {
							authorName: reply.author?.displayName || formatMessage(commentsi18n.anonymous),
							timestamp: formatTimestampForAriaLabel({
								intl,
								dateString: reply.createdAtNonLocalized,
								isReply: true,
							}),
						})}
					>
						<div css={[!replyIsBeingEdited && objectCommentWrapperStyles]}>
							{/* The editor has its own bottom padding to help with the left border */}
							<ObjectComment
								comment={reply}
								supportedTopLevelActions={supportedTopLevelActions}
								pageInfo={pageInfo}
								pageMode={pageMode}
								isUnread={unreadCommentIDSet.has(reply.id)}
								threadKey={threadKey}
								// If we have the left border, don't show the branching style
								hideBranchingStyle={hideBranchingStyle}
								adjustForNestedReplies={false}
								canAddComments={canAddComments}
								isParentCommentOpen={isParentCommentOpen}
								adjustForFooterNestedReplies={false}
								topLevelParentCommentId={parentComment.id}
								commentHasChildren={replyHasChildren}
								isLastNestedFooterReply={isLastNestedFooterReply}
								flags={flags}
								showRepliesWithCurvedBorders={false}
								replyHasNestedObjects={replyHasNestedObjects}
							/>
						</div>
						{showReplyEditor && (
							<div
								css={[
									nestedReplyThreadBoxStyles,
									replyHasChildren && withLeftBorderStyle,
									replyHasChildren &&
										fg('confluence_comment_border_migration') &&
										withLeftBorderStyleUpdated,
									replyHasChildren &&
										expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
										nestedReplyThreadBoxStylesV2,
								]}
							>
								<ObjectCommentReplyEditor
									pageInfo={pageInfo}
									pageMode={pageMode}
									setReplyView={setReplyView}
									parentComment={parentComment}
									threadKey={threadKey}
									replyToId={reply.id}
									isOffline={isOffline}
									customBeforeLeftForNestedReply={adjustForFooterNestedReplies}
								/>
							</div>
						)}
						{parentToRepliesMap[reply.id]?.length > 0 && (
							<ObjectCommentNestableReplies
								parentReplyId={reply.id}
								parentToRepliesMap={parentToRepliesMap}
								canAddComments={canAddComments}
								pageInfo={pageInfo}
								pageMode={pageMode}
								threadKey={threadKey}
								supportedTopLevelActions={supportedTopLevelActions}
								isTopLevelReply={false}
								parentComment={parentComment}
								parentReplyButtonAllowed={false}
								setReplyView={setReplyView}
								isOffline={isOffline}
								flags={flags}
								isEditorExpanded={isEditorExpanded}
							/>
						)}
					</div>
				);
			})}
		</div>
	);
};
